import { useState, useMemo, useEffect } from "react";
import styles from "./borrowingCapacity.module.css";
import BorrowingCapacityResult from "./borrowingCapacityResult";
import BorrowingCapacityApllicantInfo1 from "./borrowingCapacityApllicantInfo1";
import BorrowingCapacityApllicantInfo2 from "./borrowingCapacityApllicantInfo2";

const ZAPIER_WEBHOOK = "https://hooks.zapier.com/hooks/catch/17916584/uhubf7p/";

/** ────────────────────────────────────────────────────────────────
 * Lead Gate (modal overlay, 2 steps)
 * Step 1: Qualifiers (goal/timeframe/location)
 * Step 2: Contact (name/email/phone + consent)
 * Posts (FormData, top-level keys) to Zapier with no-cors, then reveals result.
 * ─────────────────────────────────────────────────────────────── */
function LeadGate({ onComplete, onCancel, calc }) {
  const [step, setStep] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  // Step 1
  const [goal, setGoal] = useState("");
  const [timeframe, setTimeframe] = useState("");
  const [location, setLocation] = useState("");

  // Step 2
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // Meta
  const [pageUrl, setPageUrl] = useState("");
  const [utm, setUtm] = useState({
    utm_source: undefined,
    utm_medium: undefined,
    utm_campaign: undefined,
    utm_term: undefined,
    utm_content: undefined,
    gclid: undefined,
    fbclid: undefined,
    msclkid: undefined,
  });

  // Anti-abuse
  const [honeypot, setHoneypot] = useState(""); // must remain empty
  const [lastSubmitAt, setLastSubmitAt] = useState(0); // de-dupe fast clicks

  useEffect(() => {
    if (typeof window !== "undefined") {
      setPageUrl(window.location.href);

      // Body scroll lock while modal is open
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      // Mobile breakpoint watcher
      const mq = window.matchMedia("(max-width: 520px)");
      const update = () => setIsMobile(mq.matches);
      update();
      mq.addEventListener?.("change", update);

      // Capture UTM params if present
      const params = new URLSearchParams(window.location.search);
      const grab = (k) => (params.get(k) || undefined);
      setUtm({
        utm_source: grab("utm_source"),
        utm_medium: grab("utm_medium"),
        utm_campaign: grab("utm_campaign"),
        utm_term: grab("utm_term"),
        utm_content: grab("utm_content"),
        gclid: grab("gclid"),
        fbclid: grab("fbclid"),
        msclkid: grab("msclkid"),
      });

      return () => {
        document.body.style.overflow = prev;
        mq.removeEventListener?.("change", update);
      };
    }
  }, []);

  const REQUIRED = (v) => (typeof v === "string" ? v.trim() !== "" : !!v);
  const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const isAUPhone = (v) => /^(\+?61|0)[2-478]\d{8}$/.test(v.replace(/\s+/g, ""));

  const step1Valid = useMemo(
    () => REQUIRED(goal) && REQUIRED(timeframe) && REQUIRED(location),
    [goal, timeframe, location]
  );
  const step2Valid = useMemo(
    () => REQUIRED(name) && isAUPhone(mobile) && isEmail(email) && consent,
    [name, mobile, email, consent]
  );

  const append = (fd, key, val) => {
    if (val === undefined || val === null) return;
    fd.append(key, String(val));
  };

  async function handleSubmit() {
    setErr("");
    if (!step2Valid) return;

    // De-dupe fast double clicks (2s)
    const now = Date.now();
    if (now - lastSubmitAt < 2000) return;
    setLastSubmitAt(now);

    // Honeypot: if bot filled the hidden field, block
    if (honeypot) {
      setErr("Submission blocked.");
      return;
    }

    setSubmitting(true);
    try {
      const appType = (calc?.numberOfApplicants ?? 1) > 1 ? "Joint" : "Single";

      // Build FormData with EXACT top-level keys you requested
      const fd = new FormData();

      // Lead / identifiers
      append(fd, "source", "Borrowing Power");
      append(fd, "full_name", name);
      append(fd, "email", email);
      append(fd, "phone_mobile", mobile);
      append(fd, "consent", consent ? "Yes" : "No");

      // Qualifiers
      append(fd, "goal", goal);
      append(fd, "timeframe", timeframe);
      append(fd, "location", location);

      // Calculator inputs
      append(fd, "applicants_count", calc?.numberOfApplicants ?? 1);
      append(fd, "application_type", appType);
      append(fd, "dependants_count", calc?.numberOfDependant ?? 0);
      append(fd, "income_primary_annual", calc?.annualIncome1 ?? 0);
      append(fd, "income_secondary_annual", calc?.annualIncome2 ?? 0);
      append(fd, "rental_income_monthly", calc?.monthlyRentalIncome ?? 0);
      append(fd, "credit_card_limit", calc?.creditCardLimit ?? 0);
      append(fd, "personal_loan_repayment_monthly", calc?.personalLoanRepay ?? 0);
      append(fd, "home_loan_repayment_monthly", calc?.homeLoanRepay ?? 0);
      append(fd, "interest_rate_percent", calc?.rate ?? 0);
      append(fd, "loan_term_years", calc?.year ?? 0);
      append(fd, "serviceability_buffer_percent", calc?.serviceBuffer ?? 0);

      // Meta (top-level UTMs + page_url + UA + timestamps + honeypot)
      append(fd, "page_url", pageUrl);
      append(fd, "utm_source", utm.utm_source);
      append(fd, "utm_medium", utm.utm_medium);
      append(fd, "utm_campaign", utm.utm_campaign);
      append(fd, "utm_term", utm.utm_term);
      append(fd, "utm_content", utm.utm_content);
      append(fd, "gclid", utm.gclid);
      append(fd, "fbclid", utm.fbclid);
      append(fd, "msclkid", utm.msclkid);
      append(fd, "submitted_at", new Date().toISOString());
      append(
        fd,
        "user_agent",
        typeof navigator !== "undefined" ? navigator.userAgent : ""
      );
      append(fd, "honeypot", honeypot); // should be empty

      // Fire-and-forget; no headers and no-cors to avoid preflight/CORS blocks
      await fetch(ZAPIER_WEBHOOK, {
        method: "POST",
        body: fd,
        mode: "no-cors",
        keepalive: true,
      });

      // Reveal result (response is opaque in no-cors, so proceed)
      onComplete?.({
        source: "Borrowing Power",
        name,
        email,
        mobile,
        consent: consent ? "Yes" : "No",
        goal,
        timeframe,
        location,
        applicationType: appType,
        calc: { ...calc },
        pageUrl,
        createdAt: new Date().toISOString(),
      });
    } catch (e) {
      // Soft-fail: proceed to result (don’t block UX)
      console.warn("Zap submit failed; proceeding:", e);
      onComplete?.({
        source: "Borrowing Power",
        name,
        email,
        mobile,
        consent: consent ? "Yes" : "No",
        goal,
        timeframe,
        location,
        applicationType: (calc?.numberOfApplicants ?? 1) > 1 ? "Joint" : "Single",
        calc: { ...calc },
        pageUrl,
        createdAt: new Date().toISOString(),
        note: "Client-side proceed after error",
      });
    } finally {
      setSubmitting(false);
    }
  }

  // Mobile-aware compact UI (short headline + short CTA)
  const ui = {
    backdrop: {
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "grid", placeItems: "center", zIndex: 9999
    },
    card: {
      width: "min(760px, 94vw)", background: "#fff", borderRadius: 16,
      boxShadow: "0 12px 28px rgba(0,0,0,0.18)", overflow: "hidden"
    },
    header: {
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: isMobile ? "12px 16px" : "16px 20px",
      background: "#F7F8F8", borderBottom: "1px solid #eee"
    },
    title: {
      margin: 0,
      fontSize: isMobile ? 18 : 22,
      lineHeight: 1.2,
      color: "#141414",
      fontWeight: 800,
      maxWidth: "90%",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    body: { padding: isMobile ? 16 : 20 },
    subtitle: { margin: "0 0 12px", color: "#333", fontSize: isMobile ? 13 : 14 },
    label: { display: "block", margin: "12px 0 6px", fontWeight: 600, color: "#141414", fontSize: 14 },
    chipRow: { display: "flex", flexWrap: "wrap", gap: 8 },
    chip: {
      padding: isMobile ? "8px 12px" : "10px 14px",
      borderRadius: 999, border: "1px solid #dcdcdc",
      background: "#fff", cursor: "pointer", fontWeight: 500, fontSize: isMobile ? 14 : 15
    },
    chipSelected: {
      borderColor: "#4EDCF6", boxShadow: "0 0 0 2px rgba(78,220,246,0.25) inset"
    },
    input: {
      width: "100%", height: isMobile ? 44 : 48, padding: isMobile ? "8px 12px" : "10px 14px",
      borderRadius: 12, border: "1px solid #dcdcdc",
      background: "#fff", fontSize: isMobile ? 14 : 15, outline: "none"
    },
    inputRow2: {
      display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16
    },
    row: {
      display: "flex", justifyContent: "space-between", gap: 12, marginTop: 18, alignItems: "center"
    },
    btnPri: {
      padding: isMobile ? "10px 14px" : "12px 18px",
      minHeight: isMobile ? 44 : 48,
      borderRadius: 12, fontWeight: 700, fontSize: isMobile ? 15 : 16,
      border: "1px solid #141414", background: "#141414", color: "#fff", cursor: "pointer",
      whiteSpace: "nowrap"
    },
    btnSec: {
      padding: isMobile ? "10px 14px" : "12px 18px",
      minHeight: isMobile ? 44 : 48,
      borderRadius: 12, fontWeight: 700, fontSize: isMobile ? 15 : 16,
      border: "1px solid #dcdcdc", background: "#fff", color: "#141414", cursor: "pointer"
    },
    error: { marginTop: 12, color: "#b00020", fontWeight: 600, fontSize: 14 },
  };

  return (
    <div style={ui.backdrop} role="dialog" aria-modal="true">
      <div style={ui.card}>
        <div style={ui.header}>
          <h3 style={ui.title}>See your borrowing power</h3>
          <button
            aria-label="Close"
            onClick={onCancel}
            style={{ background: "transparent", border: 0, fontSize: 24, lineHeight: 1, cursor: "pointer" }}
          >
            ×
          </button>
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <div style={ui.body}>
            <p style={ui.subtitle}>One quick step to personalise your result.</p>

            <label style={ui.label}>What are you hoping to do?</label>
            <div style={ui.chipRow}>
              {["Get pre-approval", "Check my borrowing power", "Compare lenders"].map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGoal(g)}
                  style={{ ...ui.chip, ...(goal === g ? ui.chipSelected : {}) }}
                >
                  {g}
                </button>
              ))}
            </div>

            <label style={ui.label}>When are you looking to buy?</label>
            <div style={ui.chipRow}>
              {["ASAP (0–3 months)", "3–6 months", "6+ months / just researching"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTimeframe(t)}
                  style={{ ...ui.chip, ...(timeframe === t ? ui.chipSelected : {}) }}
                >
                  {t}
                </button>
              ))}
            </div>

            <label style={ui.label}>Where are you looking?</label>
            <input
              style={ui.input}
              placeholder="e.g. Brisbane, QLD"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />

            <div style={ui.row}>
              <button style={ui.btnSec} onClick={onCancel}>Back</button>
              <button
                style={{ ...ui.btnPri, opacity: step1Valid ? 1 : 0.5, cursor: step1Valid ? "pointer" : "not-allowed" }}
                onClick={() => step1Valid && setStep(2)}
                disabled={!step1Valid}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div style={ui.body}>
            <label style={ui.label}>Full name</label>
            <input
              style={ui.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Alex Smith"
            />

            <div style={{ marginTop: 12 }} />

            <div style={ui.inputRow2}>
              <div>
                <label style={ui.label}>Email</label>
                <input
                  style={ui.input}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@email.com"
                  inputMode="email"
                />
              </div>
              <div>
                <label style={ui.label}>Phone</label>
                <input
                  style={ui.input}
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="04xx xxx xxx"
                  inputMode="tel"
                />
              </div>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span>By continuing, you agree we may contact you about your home loan options.</span>
            </label>

            {/* Honeypot (hidden) */}
            <input
              style={{ display: "none" }}
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />

            {err && <div style={ui.error}>{err}</div>}

            <div style={ui.row}>
              <button style={ui.btnSec} onClick={() => setStep(1)}>Back</button>
              <button
                style={{ ...ui.btnPri, opacity: step2Valid && !submitting ? 1 : 0.5, cursor: step2Valid && !submitting ? "pointer" : "not-allowed" }}
                onClick={handleSubmit}
                disabled={!step2Valid || submitting}
              >
                {submitting ? "Submitting…" : "See result"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const BorrowingCapacity = () => {
  const [numberOfApplicants, setNumberOfApplicants] = useState(1);
  const [numberOfDependant, setNumberOfDependant] = useState(0);
  const [annualIncome1, setAnnualIncome1] = useState(0);
  const [annualIncome2, setAnnualIncome2] = useState(0);
  const [monthlyRentalIncome, setMonthlyRentalIncome] = useState(0);
  const [creditCardLimit, setCreditCardLimit] = useState(0);
  const [personalLoanRepay, setPersonalLoanRepay] = useState(0);
  const [homeLoanRepay, setHomeLoanRepay] = useState(0);
  const [rate, setRate] = useState(4.79);
  const [year, setYear] = useState(30);
  const [serviceBuffer, setServiceBuffer] = useState(3);
  const [displayPart, setDisplayPart] = useState(1);

  // lead-modal open/close + payload
  const [leadOpen, setLeadOpen] = useState(false);
  const [lead, setLead] = useState(null);

  const dependants = Array.from({ length: 11 }, (_, index) => index);
  const years = Array.from({ length: 30 }, (_, index) => index + 1);

  const onNumberOfApplicantsChange = (applicants) => setNumberOfApplicants(applicants);
  const onNumberOfDependantChange = (numberOfDep) => setNumberOfDependant(numberOfDep);
  const onRateChange = (rateChanged) => setRate(rateChanged);
  const onYearsChange = (yearsChanged) => setYear(yearsChanged);
  const onAnnualIncome1 = (newValue) => setAnnualIncome1(newValue);
  const onAnnualIncome2 = (newValue) => setAnnualIncome2(newValue);
  const onCreditCardChange = (newValue) => setCreditCardLimit(newValue);
  const onHomeLoanRepayChange = (newValue) => setHomeLoanRepay(newValue);
  const onRentalIncomeChange = (newValue) => setMonthlyRentalIncome(newValue);
  const onPersonalLoanChange = (newValue) => setPersonalLoanRepay(newValue);
  const setDisplayPartChange = (part) => setDisplayPart(part);

  // Click "Show Result" -> open the gate
  const handleShowResult = () => setLeadOpen(true);

  // After successful submit -> close gate, dispatch existing event to reveal snapshot
  const revealResult = (leadPayload) => {
    setLead(leadPayload);
    setLeadOpen(false);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("bc-show-result", { detail: leadPayload }));
    }
  };

  return (
    <>
      <div className={`row g-6 ${styles.main_wrapper}`}>
        <div className={`row g-3 ${styles.data_wrapper}`}>
          {displayPart === 1 && (
            <BorrowingCapacityApllicantInfo1
              numberOfApplicants={numberOfApplicants}
              onNumberOfApplicantsChange={onNumberOfApplicantsChange}
              numberOfDependant={numberOfDependant}
              dependants={dependants}
              onNumberOfDependantChange={onNumberOfDependantChange}
              rate={rate}
              onRateChange={onRateChange}
              year={year}
              years={years}
              onYearsChange={onYearsChange}
              annualIncome1={annualIncome1}
              annualIncome2={annualIncome2}
              onAnnualIncome1={onAnnualIncome1}
              onAnnualIncome2={onAnnualIncome2}
            />
          )}

          {displayPart === 2 && (
            <BorrowingCapacityApllicantInfo2
              creditCardLimit={creditCardLimit}
              onCreditCardChange={onCreditCardChange}
              homeLoanRepay={homeLoanRepay}
              onHomeLoanRepayChange={onHomeLoanRepayChange}
              monthlyRentalIncome={monthlyRentalIncome}
              onRentalIncomeChange={onRentalIncomeChange}
              personalLoanRepay={personalLoanRepay}
              onPersonalLoanChange={onPersonalLoanChange}
            />
          )}

          {/* Bottom nav row: Step 1 -> Next (right). Step 2 -> Back (left) + Show Result (right). */}
          <div className={`col-m-6 ${styles.btn_row_container}`}>
            {displayPart === 1 && (
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "12px", width: "100%" }}>
                <button className={`btn btn-secondary ${styles.btn_next}`} onClick={() => setDisplayPartChange(2)}>
                  Next
                </button>
              </div>
            )}

            {displayPart === 2 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", width: "100%" }}>
                <button className={`btn btn-secondary ${styles.btn_next}`} onClick={() => setDisplayPartChange(1)}>
                  Back
                </button>
                <button className={`btn btn-secondary ${styles.btn_next}`} onClick={handleShowResult}>
                  Show Result
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={`row g-3 ${styles.data_wrapper}`}>
          <BorrowingCapacityResult
            numberOfApplicantsIn={numberOfApplicants}
            numberOfDependantIn={numberOfDependant}
            annualIncome1In={annualIncome1}
            annualIncome2In={annualIncome2}
            monthlyRentalIncomeIn={monthlyRentalIncome}
            creditCardLimitIn={creditCardLimit}
            personalLoanRepayIn={personalLoanRepay}
            homeLoanRepayIn={homeLoanRepay}
            yearIn={year}
            rateIn={rate}
            serviceBufferIn={serviceBuffer}
            leadIn={lead}
          />
        </div>
      </div>

      {/* Lead modal */}
      {leadOpen && (
        <LeadGate
          onComplete={revealResult}
          onCancel={() => setLeadOpen(false)}
          calc={{
            numberOfApplicants,
            numberOfDependant,
            annualIncome1,
            annualIncome2,
            monthlyRentalIncome,
            creditCardLimit,
            personalLoanRepay,
            homeLoanRepay,
            rate,
            year,
            serviceBuffer,
          }}
        />
      )}
    </>
  );
};

export default BorrowingCapacity;
